import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Form,
  FormIntegrationDto,
  FormRecipient,
  FormRejectionRow,
  FormSummary,
  IntegrationResponse,
  MessageResponse,
  Problem,
} from '@/api';
import { isSpamFor, useVerdictPreference } from '@/lib/message-prefs';
import { useRustyState } from '@/state';

const QUIET = { onSuccess: false, onError: false } as const;
const RECENT_MESSAGES = 5;
const REJECTION_DAYS = 7;

export type BlockStatus = 'loading' | 'ready' | 'failed';

type Block =
  | 'summary'
  | 'problems'
  | 'recipients'
  | 'rejections'
  | 'links'
  | 'integrations'
  | 'messages';

export interface FormOverviewData {
  form: Form | null;
  loading: boolean;
  notFound: boolean;
  /** Null while loading or when the endpoint is not available. */
  summary: FormSummary | null;
  problems: Problem[] | null;
  recipients: FormRecipient[] | null;
  rejections: FormRejectionRow[] | null;
  links: FormIntegrationDto[] | null;
  integrations: IntegrationResponse[] | null;
  messages: MessageResponse[] | null;
  status: Record<Block, BlockStatus>;
}

export interface FormOverview extends FormOverviewData {
  reloadForm: () => Promise<void>;
  reloadRecipients: () => Promise<void>;
  reloadLinks: () => Promise<void>;
  /** Problems, summary, rejections and recent messages: what a save or a test submission can change. */
  reloadStatus: () => Promise<void>;
}

const EMPTY: FormOverviewData = {
  form: null,
  loading: true,
  notFound: false,
  summary: null,
  problems: null,
  recipients: null,
  rejections: null,
  links: null,
  integrations: null,
  messages: null,
  status: {
    summary: 'loading',
    problems: 'loading',
    recipients: 'loading',
    rejections: 'loading',
    links: 'loading',
    integrations: 'loading',
    messages: 'loading',
  },
};

const settled = (r: PromiseSettledResult<unknown>): BlockStatus =>
  r.status === 'fulfilled' ? 'ready' : 'failed';

/** Every source the form overview reads; each one fails on its own, so a missing endpoint leaves its section empty. */
export function useFormOverview(formId: string | undefined): FormOverview {
  const [data, setData] = useState<FormOverviewData>(EMPTY);
  const [verdict] = useVerdictPreference();
  const verdictRef = useRef(verdict);
  verdictRef.current = verdict;
  const patch = useCallback(
    (
      next: Partial<Omit<FormOverviewData, 'status'>>,
      status: Partial<FormOverviewData['status']> = {}
    ) => {
      setData((prev) => ({ ...prev, ...next, status: { ...prev.status, ...status } }));
    },
    []
  );

  const reloadForm = useCallback(async () => {
    if (!formId) return;
    try {
      const form = await useRustyState.getState().api.getForm(formId);
      patch({ form, loading: false, notFound: false });
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      patch({ loading: false, notFound: status === 404 || status === 400 || status === 403 });
    }
  }, [formId, patch]);

  const reloadRecipients = useCallback(async () => {
    if (!formId) return;
    try {
      const res = await useRustyState.getState().api.getFormRecipients(formId);
      patch({ recipients: res.data }, { recipients: 'ready' });
    } catch {
      patch({ recipients: null }, { recipients: 'failed' });
    }
  }, [formId, patch]);

  const reloadLinks = useCallback(async () => {
    if (!formId) return;
    const { api } = useRustyState.getState();
    const [links, integrations] = await Promise.allSettled([
      api.listFormIntegrations(formId),
      api.getAllIntegrations(),
    ]);
    patch(
      {
        links: links.status === 'fulfilled' ? links.value.data : null,
        integrations: integrations.status === 'fulfilled' ? integrations.value : null,
      },
      { links: settled(links), integrations: settled(integrations) }
    );
  }, [formId, patch]);

  const reloadStatus = useCallback(async () => {
    if (!formId) return;
    const state = useRustyState.getState();
    const [problems, summaries, rejections, messages] = await Promise.allSettled([
      state.api.getProblems({ form_id: formId }, QUIET),
      state.refreshFormsSummary(),
      state.api.getFormRejections(formId, REJECTION_DAYS, QUIET),
      state.api.listMessages({
        form_id: formId,
        limit: RECENT_MESSAGES,
        offset: 0,
        is_spam: isSpamFor(verdictRef.current),
      }),
    ]);
    patch(
      {
        problems: problems.status === 'fulfilled' ? problems.value.data : null,
        summary:
          summaries.status === 'fulfilled'
            ? (summaries.value?.find((s) => s.id === formId) ?? null)
            : null,
        rejections: rejections.status === 'fulfilled' ? rejections.value.rows : null,
        messages: messages.status === 'fulfilled' ? messages.value.data : null,
      },
      {
        problems: settled(problems),
        summary: settled(summaries),
        rejections: settled(rejections),
        messages: settled(messages),
      }
    );
  }, [formId, patch]);

  useEffect(() => {
    setData(EMPTY);
    void reloadForm();
    void reloadRecipients();
    void reloadLinks();
    void reloadStatus();
  }, [reloadForm, reloadRecipients, reloadLinks, reloadStatus]);

  const shownVerdict = useRef(verdict);
  useEffect(() => {
    if (!formId || shownVerdict.current === verdict) return;
    shownVerdict.current = verdict;
    let live = true;
    patch({}, { messages: 'loading' });
    useRustyState
      .getState()
      .api.listMessages({
        form_id: formId,
        limit: RECENT_MESSAGES,
        offset: 0,
        is_spam: isSpamFor(verdict),
      })
      .then((res) => live && patch({ messages: res.data }, { messages: 'ready' }))
      .catch(() => live && patch({ messages: null }, { messages: 'failed' }));
    return () => {
      live = false;
    };
  }, [formId, verdict, patch]);

  return { ...data, reloadForm, reloadRecipients, reloadLinks, reloadStatus };
}
